import { useState } from 'react';
import { css } from '@emotion/react';
import {
  color,
  spacing,
  typography,
  Button,
  RadioButton,
  Typography
} from '@uniquegood/realworld-studio-design';
import ConfettiIcon from '../../../../public/assets/icons/confetti.svg';
import SchoolIcon from '../../../../public/assets/icons/school.svg';
import DotsCircleIcon from '../../../../public/assets/icons/dots-circle.svg';
import UserIcon from '../../../../public/assets/icons/user.svg';
import PurposeTextarea from './PurposeTextarea';

interface SelectChannelPurposeProps {
  onSubmit: (purpose: UsePurposeType, otherText?: string) => void;
}

const PURPOSE_OPTIONS = [
  {
    value: 'Personal' as const,
    icon: UserIcon,
    title: '개인용',
    description: '취미, 놀이 목적의 콘텐츠를 만들어요'
  },
  {
    value: 'Campaign' as const,
    icon: ConfettiIcon,
    title: '캠페인',
    description: '행사, 이벤트를 위한 콘텐츠를 만들어요'
  },
  {
    value: 'Education' as const,
    icon: SchoolIcon,
    title: '교육',
    description: '수업 또는 교육용 콘텐츠를 만들어요'
  },
  {
    value: 'Etc' as const,
    icon: DotsCircleIcon,
    title: '기타 목적',
    description: null
  }
];

export default function SelectChannelPurpose({ onSubmit }: SelectChannelPurposeProps) {
  const [selectedPurpose, setSelectedPurpose] = useState<UsePurposeType>('Personal');
  const [otherText, setOtherText] = useState('');

  const handleSubmit = () => {
    if (!selectedPurpose) return;

    if (selectedPurpose === 'Etc' && !otherText.trim()) {
      return;
    }

    onSubmit(selectedPurpose, selectedPurpose === 'Etc' ? otherText : undefined);
  };

  const isButtonDisabled = !selectedPurpose || (selectedPurpose === 'Etc' && !otherText.trim());

  return (
    <div css={container}>
      <div css={headerContainer}>
        <Typography type="displayLarge">채널 사용 목적은</Typography>
        <Typography type="displayLarge">무엇인가요?</Typography>
      </div>

      <div css={optionsContainer}>
        {PURPOSE_OPTIONS.map((option) => {
          const isSelected = selectedPurpose === option.value;

          return (
            <div key={option.value}>
              <button
                type="button"
                css={isSelected ? optionButtonSelected : optionButton}
                onClick={() => setSelectedPurpose(option.value)}
              >
                <div css={optionContent}>
                  <div css={iconWrapper}>
                    <img src={option.icon} alt={option.title} />
                  </div>
                  <div css={textWrapper}>
                    <div css={optionTitle}>{option.title}</div>
                    {option.description && <div css={optionDescription}>{option.description}</div>}
                  </div>
                  <div css={radioWrapper}>
                    <RadioButton
                      name={option.title}
                      value={option.value}
                      checked={isSelected}
                      onChange={() => setSelectedPurpose(option.value)}
                    >
                      <></>
                    </RadioButton>
                  </div>
                </div>
              </button>

              {isSelected && option.value === 'Etc' && (
                <div css={textareaWrapper}>
                  <PurposeTextarea value={otherText} onChange={setOtherText} />
                </div>
              )}
            </div>
          );
        })}
        <Button
          disabled={isButtonDisabled}
          onClick={handleSubmit}
          size="large"
          type="primary"
          cssStyle={buttonStyle}
        >
          채널 만들기
        </Button>
      </div>
    </div>
  );
}

const container = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 40px;
  padding: 24px;
  background-color: ${color.surface_default_default};
`;

const headerContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  max-width: 320px;
`;

const optionsContainer = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 320px;
`;

const optionButton = css`
  width: 100%;
  background: ${color.surface_default_subdued};
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.medium};
  padding: 10px ${spacing.common.medium};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${color.palette_primary_purple_tint_01};
  }
`;

const optionButtonSelected = css`
  ${optionButton};
  background: ${color.palette_primary_purple_tint_01};
  border: 1px solid ${color.palette_primary_purple_100};
`;

const optionContent = css`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const iconWrapper = css`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`;

const textWrapper = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
`;

const optionTitle = css`
  color: ${color.text_default};
  font-family: ${typography.family.primary};
  font-size: ${typography.size.s3};
  font-weight: ${typography.weight.bold};
  line-height: 24px;
`;

const optionDescription = css`
  color: ${color.text_default};
  font-family: ${typography.family.primary};
  font-size: ${typography.size.s2};
  font-weight: ${typography.weight.regular};
  line-height: 20px;
`;

const radioWrapper = css`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

const textareaWrapper = css`
  margin-top: ${spacing.common.small};
`;

const buttonStyle = css`
  margin-top: ${spacing.common.medium};
  width: 100%;
`;
